import { useState } from "react";
import { ArrowRight, Brain, BarChart3, ClipboardList, Gift, LayoutDashboard, LifeBuoy, Settings, ShieldCheck, Tags, UserCheck, Users, FileText } from "lucide-react";
import { formatDkk } from "@/lib/api";

const GROUPS = [
  ["Overblik", [["metrics", "Overblik", LayoutDashboard], ["business-brain", "Business Brain", Brain], ["analytics", "Besøg og konvertering", BarChart3]]],
  ["Daglig drift", [["problems", "Forespørgsler", ClipboardList], ["experts", "Eksperter", UserCheck], ["qualifications", "Verificeringer", ShieldCheck], ["support", "Supportsager", LifeBuoy], ["users", "Brugere", Users]]],
  ["Vækst og indhold", [["referrals", "Invitationer", Gift], ["categories", "Kategorier", Tags], ["seo", "SEO-sider", FileText]]],
  ["Opsætning", [["settings", "Indstillinger", Settings]]],
];
const DESCRIPTIONS = {
  "business-brain": "Dit private kontrolcenter for økonomi, abonnementer og næste skridt.",
  metrics: "Din daglige indgang til SpørgMig. Se status og gå direkte til næste opgave.",
  analytics: "Se hvor besøgende kommer fra og hvordan de bliver til kunder.",
  problems: "Følg kundernes forespørgsler fra første spørgsmål til afsluttet opgave.",
  experts: "Gennemgå ansøgninger og administrer eksperterne på platformen.",
  qualifications: "Kontrollér dokumentation for uddannelse, certifikater og virksomheder.",
  support: "Hold styr på henvendelser og hjælp kunder og eksperter videre.",
  users: "Find konti og se hvem der bruger SpørgMig.",
  referrals: "Følg invitationer, nye eksperter og brugen af gebyrfri indtjening.",
  categories: "Organisér de fagområder kunder og eksperter kan vælge.",
  seo: "Administrer hjælpesider og deres synlighed i søgemaskiner.",
  settings: "Se og rediger platformens indstillinger.",
};

export function initialAdminTab() {
  const requested = new URLSearchParams(window.location.search).get("tab");
  return GROUPS.flatMap(([, items]) => items).some(([key]) => key === requested && key !== "business-brain") ? requested : "metrics";
}

export default function AdminWorkspace({ tab, onNavigate, pendingExperts, children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const title = GROUPS.flatMap(([, items]) => items).find(([key]) => key === tab)?.[1] || "Administration";
  return <div className="admin-workspace">
    <aside className="admin-sidebar">
      <div className="admin-sidebar-brand"><LayoutDashboard size={22} aria-hidden="true"/><div><strong>Administration</strong><span>SpørgMig</span></div></div>
      <button type="button" className="admin-mobile-toggle" aria-expanded={menuOpen} aria-controls="admin-navigation" onClick={() => setMenuOpen((open) => !open)}>{menuOpen ? "Luk menu" : "Åbn menu"}<span>{title}</span></button>
      <nav id="admin-navigation" aria-label="Administration" data-mobile-open={menuOpen}>
        {GROUPS.map(([group, items]) => <div className="admin-nav-group" key={group}>
          <p>{group}</p>
          {items.map(([key, label, Icon]) => key === "business-brain" ? <a key={key} href="/admin/business-brain" aria-current={tab === key ? "page" : undefined} className="admin-nav-item" data-testid="admin-business-brain-link"><Icon size={18} aria-hidden="true"/><span>{label}</span></a> : <button type="button" key={key} onClick={() => { onNavigate(key); setMenuOpen(false); }} aria-current={tab === key ? "page" : undefined} className="admin-nav-item" data-testid={`admin-tab-${key}`}>
            <Icon size={18} aria-hidden="true"/><span>{label}</span>
            {key === "experts" && pendingExperts > 0 && <span className="admin-nav-count" aria-label={`${pendingExperts} afventer godkendelse`}>{pendingExperts}</span>}
          </button>)}
        </div>)}
      </nav>
    </aside>
    <div className="admin-main">
      {tab !== "analytics" && <header className="admin-page-heading">
        <span className="admin-eyebrow">SpørgMig · Administration</span>
        <h1>{title}</h1>
        <p>{DESCRIPTIONS[tab]}</p>
      </header>}
      <div className="admin-content" data-admin-section={tab} key={tab}>{children}</div>
    </div>
  </div>;
}

export function AdminOverview({ metrics, pendingExperts, waitingRequests, onNavigate }) {
  const money = (value) => value == null ? "—" : formatDkk(value);
  const percent = (value) => value == null ? "—" : `${value}%`;
  return <div className="admin-overview">
    <section className="admin-priorities" aria-labelledby="admin-priorities-title">
      <div className="admin-section-heading"><div><h2 id="admin-priorities-title">Start her</h2><p>Gå direkte til dagens arbejde.</p></div><span className="admin-section-tag">Daglig drift</span></div>
      <div className="admin-action-grid">
        <Action icon={UserCheck} title="Ekspertansøgninger" value={pendingExperts} description="Afventer din godkendelse" onClick={() => onNavigate("experts")}/>
        <Action icon={ClipboardList} title="Åbne forespørgsler" value={waitingRequests} description="Afventer næste skridt i forløbet" onClick={() => onNavigate("problems")}/>
        <Action icon={LifeBuoy} title="Supportsager" description="Se henvendelser og følg op" onClick={() => onNavigate("support")}/>
      </div>
    </section>
    <section aria-labelledby="admin-numbers-title">
      <div className="admin-section-heading"><div><h2 id="admin-numbers-title">Platformen i tal</h2><p>Samlede nøgletal. Vælg Besøg og konvertering for statistik over en bestemt periode.</p></div></div>
      <div className="admin-metric-grid">
        <Metric label="Forespørgsler" value={metrics.problems_submitted} description="Indsendte spørgsmål og opgaver"/>
        <Metric label="Bookinger" value={metrics.bookings} description="Registrerede bookinger"/>
        <Metric label="Aktive eksperter" value={metrics.active_experts} description="Platformens aktive eksperter"/>
        <Metric label="Kunder" value={metrics.customers} description="Registrerede kunder"/>
      </div>
    </section>
    <section className="admin-economy" aria-labelledby="admin-economy-title">
      <div className="admin-section-heading"><div><h2 id="admin-economy-title">Økonomi og resultater</h2><p>Ordreværdi og platformens indtægt vises hver for sig.</p></div></div>
      <div className="admin-metric-grid admin-metric-grid-three">
        <Metric label="Samlet ordreværdi" value={money(metrics.gmv_dkk)} description="Samlet handelsværdi (GMV)"/>
        <Metric label="Platformens omsætning" value={money(metrics.platform_revenue_dkk)} description="Registreret omsætning for SpørgMig"/>
        <Metric label="Gennemsnitlig ordre" value={money(metrics.aov_dkk)} description="Gennemsnitlig værdi pr. ordre"/>
      </div>
      <div className="admin-rates">
        <Metric label="Konverteringsrate" value={percent(metrics.conversion_rate_pct)}/>
        <Metric label="Gennemførelsesrate" value={percent(metrics.completion_rate_pct)}/>
        <button type="button" className="admin-inline-link" onClick={() => onNavigate("analytics")}>Se besøg og konvertering <ArrowRight size={17} aria-hidden="true"/></button>
      </div>
    </section>
  </div>;
}

function Action({ icon: Icon, title, value, description, onClick }) {
  return <button type="button" className="admin-action-card" onClick={onClick}>
    <span className="admin-action-icon"><Icon size={22} aria-hidden="true"/></span>
    <span className="admin-action-copy"><strong>{title}</strong>{value !== undefined && <b>{value ?? "—"}</b>}<small>{description}</small></span>
    <ArrowRight size={18} aria-hidden="true"/>
  </button>;
}

function Metric({ label, value, description }) {
  return <div className="admin-metric"><span>{label}</span><strong>{value ?? "—"}</strong>{description && <small>{description}</small>}</div>;
}
